import React, {Component} from 'react';
import AdminReadyButton from 'components/AdminReadyButton';
import strings from 'strings';
import {getNonwordRepetition} from 'constants/tests';
import {updateReady, updateStepData} from 'api';
import {ENVIRONMENT} from '../../constants/index';

const NW_STAGES = {
	INTRO: 0,
	TRIAL: 1,
	WAITING_POST_TRIAL: 2,
	TEST: 3,
	WAITING: 4,
};

const TRIAL_ITEMS_COUNT = 1;
const DELAY_SECONDS = 10; // must match Test.js delay
// Stop after the current item is complete when six immediate errors are consecutive.
const MAX_IMMEDIATE_CONSECUTIVE_ERRORS = 6;

class NonwordRepetitionAdmin extends Component {
	constructor(props) {
		super(props);

		const spec = getNonwordRepetition(
			this.props.testKeyType,
			this.props.ageGroup
		);

		this.trialItems =
			(spec &&
				spec.getTrialSteps(
					this.props.testKeyType,
					this.props.ageGroup
				)) ||
			[];
		this.testItems =
			(spec &&
				spec.getNonwordSteps(
					this.props.testKeyType,
					this.props.ageGroup
				)) ||
			[];

		this.state = {
			...this.getInitialState(props),
		};

		this.rootEl = null;
		this.delayTimer = null; // handle for setInterval
	}

	componentDidMount() {
		updateReady(false);
		if (this.rootEl) this.rootEl.focus();
	}

	componentDidUpdate(prevProps) {
		// keep stage/step in sync with roomConfig coming from TestAdmin
		if (
			prevProps.currentStage !== this.props.currentStage ||
			prevProps.currentStep !== this.props.currentStep
		) {
			// New item or stage → reset admin delay timer
			if (this.delayTimer) {
				clearInterval(this.delayTimer);
				this.delayTimer = null;
			}

			if (this.isFreshAttempt(this.props)) {
				// Returning to intro step 0 means this Nonword attempt should start clean.
				updateStepData({
					answered: false,
					correct: null,
					feedbackDone: false,
					nonwordImmediateDone: false,
				});

				this.setState(this.getInitialState(this.props));
				return;
			}

			this.setState((prevState) => ({
				currentStage: this.props.currentStage,
				currentStep: this.props.currentStep,
				activePhase: 'immediate',
				delayActive: false,
				delayRemaining: 0,
				immediateConsecutiveErrors:
					this.props.currentStage === NW_STAGES.TEST
						? prevState.immediateConsecutiveErrors
						: 0,
				disableDelayButtons:
					ENVIRONMENT === 'production' ? true : false,
			}));
		}
	}

	componentWillUnmount() {
		if (this.delayTimer) {
			clearInterval(this.delayTimer);
		}
	}

	getInitialState = (props = this.props) => ({
		currentStage: props.currentStage || NW_STAGES.INTRO,
		currentStep: props.currentStep || 0,
		activePhase: 'immediate', // 'immediate' | 'delayed'
		results: {},
		trialResults: {},
		immediateConsecutiveErrors: 0,
		delayActive: false,
		delayRemaining: 0, // seconds
		disableDelayButtons: ENVIRONMENT === 'production' ? true : false,
	});

	isFreshAttempt = (props) =>
		(props.currentStage || NW_STAGES.INTRO) === NW_STAGES.INTRO &&
		(props.currentStep || 0) === 0;

	codeToScore = (code) => (code === 'A' ? 1 : 0);

	// Count immediate errors backwards so changing a previous answer updates the stop check.
	getImmediateConsecutiveErrors = (results, currentStep) => {
		let count = 0;

		for (let index = currentStep; index >= 0; index -= 1) {
			const item = results[index];
			const code = item && item.immediate && item.immediate.code;

			if (!code || code === 'A') break;
			count += 1;
		}

		return count;
	};

	setTrialScore = (phase, code) => {
		this.setState((prev) => {
			const {currentStep, currentStage} = prev;
			const prevItem = prev.trialResults[currentStep] || {};

			const isFirstImmediate =
				currentStage === NW_STAGES.TRIAL &&
				phase === 'immediate' &&
				(!prevItem.immediate || !prevItem.immediate.code);

			if (isFirstImmediate) {
				// tell candidate to start 15s distraction, and show admin timer
				updateStepData({nonwordImmediateDone: true});
				this.startDelayTimer();
			}
			const newPhaseObj = {
				code,
				score: this.codeToScore(code),
			};
			const merged = {
				...prevItem,
				[phase]: newPhaseObj,
			};
			const tot =
				(merged.immediate ? merged.immediate.score : 0) +
				(merged.delayed ? merged.delayed.score : 0);

			return {
				trialResults: {
					...prev.trialResults,
					[currentStep]: {...merged, total: tot},
				},
				activePhase: phase,
			};
		});
	};

	hasBothTrialAnswers = (idx) => {
		const item = this.state.trialResults[idx];
		return item && item.immediate && item.delayed;
	};

	goNextTrialItem = () => {
		const {currentStep} = this.state;
		const isLast = currentStep >= TRIAL_ITEMS_COUNT - 1;

		if (isLast) {
			// finished all 3 øvingsoppgaver → go to pause / test
			this.props.nextStage();
		} else {
			this.props.nextStep();
		}
	};

	setScore = (phase, code) => {
		let shouldStopAfterItem = false;
		let shouldStartDelay = false;

		this.setState(
			(prev) => {
				const {currentStep, currentStage} = prev;
				const prevItem = prev.results[currentStep] || {};

				const isImmediateTestScore =
					currentStage === NW_STAGES.TEST && phase === 'immediate';
				const isFirstImmediate =
					isImmediateTestScore &&
					(!prevItem.immediate || !prevItem.immediate.code);

				const newPhaseObj = {
					code,
					score: this.codeToScore(code),
				};
				const merged = {
					...prevItem,
					[phase]: newPhaseObj,
				};
				const tot =
					(merged.immediate ? merged.immediate.score : 0) +
					(merged.delayed ? merged.delayed.score : 0);
				const updatedResults = {
					...prev.results,
					[currentStep]: {...merged, total: tot},
				};
				const immediateConsecutiveErrors =
					currentStage === NW_STAGES.TEST && merged.immediate
						? this.getImmediateConsecutiveErrors(
								updatedResults,
								currentStep
						  )
						: prev.immediateConsecutiveErrors;
				const itemComplete = Boolean(merged.immediate && merged.delayed);

				shouldStopAfterItem =
					currentStage === NW_STAGES.TEST &&
					itemComplete &&
					merged.immediate.code !== 'A' &&
					immediateConsecutiveErrors >=
						MAX_IMMEDIATE_CONSECUTIVE_ERRORS;
				shouldStartDelay =
					isFirstImmediate && !shouldStopAfterItem;

				return {
					results: updatedResults,
					activePhase: phase,
					immediateConsecutiveErrors,
				};
			},
			() => {
				if (shouldStopAfterItem) {
					// Stop only after both repetitions for the item are scored.
					this.stopDelayTimer();
					updateStepData({nonwordImmediateDone: false});
					this.props.submitResults(this.state.results);
					this.props.nextStage();
				} else if (shouldStartDelay) {
					this.startDelayTimer();
					updateStepData({nonwordImmediateDone: true});
				}
			}
		);
	};

	hasBothAnswers = (idx) => {
		const item = this.state.results[idx];
		return item && item.immediate && item.delayed;
	};

	startDelayTimer = () => {
		// only used in TEST stage
		if (this.delayTimer) {
			clearInterval(this.delayTimer);
		}

		this.setState({
			delayActive: true,
			delayRemaining: DELAY_SECONDS,
		});

		this.delayTimer = setInterval(() => {
			this.setState((prev) => {
				const next = prev.delayRemaining - 1;
				if (next <= 0) {
					if (this.delayTimer) {
						clearInterval(this.delayTimer);
						this.delayTimer = null;
					}
					return {
						delayRemaining: 0,
						delayActive: false,
						disableDelayButtons: false,
					};
				}
				return {delayRemaining: next};
			});
		}, 1000);
	};

	stopDelayTimer = () => {
		if (this.delayTimer) {
			clearInterval(this.delayTimer);
			this.delayTimer = null;
		}
		this.setState({
			delayActive: false,
			delayRemaining: 0,
		});
	};

	goNextItem = () => {
		this.props.nextStep(this.state.results);
	};

	// ---------- hotkeys ----------

	handleKeyDown = (e) => {
		const key = (e.key || '').toLowerCase();
		const {currentStage, activePhase} = this.state;

		const isTrial = currentStage === NW_STAGES.TRIAL;
		const isTest = currentStage === NW_STAGES.TEST;

		// Only react to keys in trial or test stage.
		if (!isTrial && !isTest) return;

		const setScoreFn = isTrial ? this.setTrialScore : this.setScore;
		const hasBothFn = isTrial
			? this.hasBothTrialAnswers
			: this.hasBothAnswers;
		const goNextFn = isTrial ? this.goNextTrialItem : this.goNextItem;

		// A/S/D score on the active phase (immediate or delayed).
		if (key === 'a') {
			setScoreFn(activePhase, 'A');
			return;
		}
		if (key === 's') {
			setScoreFn(activePhase, 'S');
			return;
		}
		if (key === 'd') {
			setScoreFn(activePhase, 'D');
			return;
		}

		// 1 / 2 switch focus between immediate and delayed
		if (key === '1') {
			this.setState({activePhase: 'immediate'});
			return;
		}
		if (key === '2') {
			this.setState({activePhase: 'delayed'});
			return;
		}

		// Enter = next item (if both answers are scored)
		if (key === 'enter') {
			const {currentStep} = this.state;
			if (hasBothFn(currentStep)) {
				goNextFn();
			}
		}
	};

	renderIntro = () => (
		<>
			<p>{strings.nonword_admin_intro_1}</p>
			<p>{strings.nonword_admin_intro_2}</p>
			<p>{strings.nonword_admin_intro_3}</p>

			<AdminReadyButton
				isPractiseTask
				remoteIsReady={true}
				onClick={this.props.nextStep}
			/>
		</>
	);

	renderTrial = () => {
		const {
			currentStep,
			activePhase,
			trialResults,
			delayActive,
			delayRemaining,
			disableDelayButtons,
		} = this.state;

		const items = this.trialItems || [];
		const total = items.length;
		const word = items[currentStep] || '';

		const itemRes = trialResults[currentStep] || {};
		const imm = itemRes.immediate || {};
		const del = itemRes.delayed || {};
		const bothAnswered = this.hasBothTrialAnswers(currentStep);

		return (
			<div>
				<p>
					{strings.nonword_admin_trial_1}
					<br />
					{strings.nonword_admin_trial_2}
				</p>
				<p>{strings.nonword_delayed_scoring_note}</p>
				<p className="sm-step-progress">
					{strings.formatString(
						strings.trialTaskNumOf || 'Øvingsoppgave {0} av {1}',
						currentStep + 1,
						total
					)}
				</p>
				<div className="sm-expected">
					<strong>
						{strings.stimulusWordLabel || 'Stimulusord'}:
					</strong>{' '}
					<span className="sm-expected__text">{word}</span>
				</div>

				<div className="nw-admin-sections">
					{/* Immediate repetition */}
					<div
						className={
							'nw-admin-section ' +
							(activePhase === 'immediate'
								? 'nw-admin-section--active'
								: '')
						}
					>
						<h4>
							{strings.nonword_immediate ||
								'1. Umiddelbar repetisjon'}
						</h4>
						<div className="nw-admin-buttons">
							<button
								type="button"
								className={
									'sm-btn ' +
									(imm.code === 'A' ? 'sm-btn--primary' : '')
								}
								onClick={() =>
									this.setTrialScore('immediate', 'A')
								}
							>
								{strings.correct || 'Riktig'}
							</button>
							<button
								type="button"
								className={
									'sm-btn ' +
									(imm.code === 'S' ? 'sm-btn--primary' : '')
								}
								onClick={() =>
									this.setTrialScore('immediate', 'S')
								}
							>
								{strings.notAnswered || 'Ikke besvart'}
							</button>
							<button
								type="button"
								className={
									'sm-btn ' +
									(imm.code === 'D' ? 'sm-btn--primary' : '')
								}
								onClick={() =>
									this.setTrialScore('immediate', 'D')
								}
							>
								{strings.wrong || 'Feil'}
							</button>
						</div>
					</div>

					{/* Delayed repetition */}
					<div
						className={
							'nw-admin-section ' +
							(activePhase === 'delayed'
								? 'nw-admin-section--active'
								: '')
						}
					>
						<h4 className="nw-admin-heading">
							<span>{strings.nonword_delayed}</span>

							{delayActive && (
								<span className="nw-admin-delay-pill">
									⏱ {delayRemaining}s
								</span>
							)}
						</h4>

						<div className="nw-admin-buttons">
							<button
								type="button"
								disabled={disableDelayButtons}
								className={
									'sm-btn ' +
									(del.code === 'A' ? 'sm-btn--primary' : '')
								}
								onClick={() =>
									this.setTrialScore('delayed', 'A')
								}
							>
								{strings.correct || 'Riktig'}
							</button>
							<button
								type="button"
								className={
									'sm-btn ' +
									(del.code === 'S' ? 'sm-btn--primary' : '')
								}
								onClick={() =>
									this.setTrialScore('delayed', 'S')
								}
								disabled={disableDelayButtons}
							>
								{strings.notAnswered}
							</button>
							<button
								type="button"
								disabled={disableDelayButtons}
								className={
									'sm-btn ' +
									(del.code === 'D' ? 'sm-btn--primary' : '')
								}
								onClick={() =>
									this.setTrialScore('delayed', 'D')
								}
							>
								{strings.wrong || 'Feil'}
							</button>
						</div>
					</div>
				</div>

				<div className="nw-admin-footer">
					<p className="nw-admin-total">
						{strings.nonword_item_total || 'Poeng for oppgaven'}:{' '}
						{(itemRes.total != null ? itemRes.total : 0) +
							' / 2'}{' '}
					</p>
					<div className={`adminItemButtons`}>
						{' '}
						<button
							type="button"
							className="sm-btn sm-btn--primary"
							disabled={!bothAnswered}
							onClick={this.goNextTrialItem}
						>
							{currentStep + 1 < total
								? strings.nextTrialTask || 'Neste øvingsoppgave'
								: strings.startTest || 'Start testoppgavene'}
						</button>
					</div>
				</div>
			</div>
		);
	};

	renderWaitingPostTrial = () => (
		<div>
			<p>
				{strings.nonword_admin_wait_post_trial ||
					'Gi eleven en kort pause. Når dere er klare, starter du testoppgavene.'}
			</p>
			<div className="lit-all-buttons-div">
				<button
					className="sm-btn sm-btn--primary"
					type="button"
					onClick={this.props.nextStage}
					style={{width: '100px'}}
				>
					{strings.start || 'Start'}
				</button>
			</div>
		</div>
	);

	renderTest = () => {
		const {
			currentStep,
			activePhase,
			results,
			delayActive,
			delayRemaining,
			disableDelayButtons,
		} = this.state;

		const items = this.testItems || [];
		const total = items.length;
		const word = items[currentStep] || '';

		const itemRes = results[currentStep] || {};
		const imm = itemRes.immediate || {};
		const del = itemRes.delayed || {};

		const bothAnswered = this.hasBothAnswers(currentStep);

		return (
			<div>
				<p>
					{strings.nonword_admin_test_info_1}
					<br />
					{strings.nonword_admin_test_info_2}
				</p>
				<p>{strings.nonword_delayed_scoring_note}</p>

				<p className="sm-step-progress">
					{strings.formatString(
						strings.taskNumOf,
						currentStep + 1,
						total
					)}
				</p>

				<div className="sm-expected">
					<strong>
						{strings.stimulusWordLabel || 'Stimulusord'}:
					</strong>{' '}
					<span className="sm-expected__text">{word}</span>
				</div>

				{word.toLowerCase() === 'klympensk' && (
					<div className="nw-alt-spelling-note">
						<span role="img" aria-label="info">
							ℹ️
						</span>{' '}
						{strings.nonword_alt_klympensk}
					</div>
				)}
				{word.toLowerCase() === 'kjasternest' && (
					<div className="nw-alt-spelling-note">
						<span role="img" aria-label="info">
							ℹ️
						</span>{' '}
						{strings.nonword_alt_kjasternest}
					</div>
				)}

				<div className="nw-admin-sections">
					{/* Immediate repetition */}
					<div
						className={
							'nw-admin-section ' +
							(activePhase === 'immediate'
								? 'nw-admin-section--active'
								: '')
						}
					>
						<h4>
							{strings.nonword_immediate ||
								'1. Umiddelbar repetisjon'}
						</h4>
						<div className="nw-admin-buttons">
							<button
								type="button"
								className={
									'sm-btn ' +
									(imm.code === 'A' ? 'sm-btn--primary' : '')
								}
								onClick={() => this.setScore('immediate', 'A')}
							>
								{strings.correct || 'Riktig'}
							</button>
							<button
								type="button"
								className={
									'sm-btn ' +
									(imm.code === 'S' ? 'sm-btn--primary' : '')
								}
								onClick={() => this.setScore('immediate', 'S')}
							>
								{strings.notAnswered || 'Ikke besvart'}
							</button>
							<button
								type="button"
								className={
									'sm-btn ' +
									(imm.code === 'D' ? 'sm-btn--primary' : '')
								}
								onClick={() => this.setScore('immediate', 'D')}
							>
								{strings.wrong || 'Feil'}
							</button>
						</div>
					</div>

					{/* Delayed repetition */}
					<div
						className={
							'nw-admin-section ' +
							(activePhase === 'delayed'
								? 'nw-admin-section--active'
								: '')
						}
					>
						<h4 className="nw-admin-heading">
							<span>
								{strings.nonword_delayed ||
									'2. Utsatt repetisjon (etter 10 sekunder)'}
							</span>

							{delayActive && (
								<span className="nw-admin-delay-pill">
									⏱ {delayRemaining}s
								</span>
							)}
						</h4>
						<div className="nw-admin-buttons">
							<button
								type="button"
								disabled={disableDelayButtons}
								className={
									'sm-btn ' +
									(del.code === 'A' ? 'sm-btn--primary' : '')
								}
								onClick={() => this.setScore('delayed', 'A')}
							>
								{strings.correct || 'Riktig'}
							</button>
							<button
								type="button"
								disabled={disableDelayButtons}
								className={
									'sm-btn ' +
									(del.code === 'S' ? 'sm-btn--primary' : '')
								}
								onClick={() => this.setScore('delayed', 'S')}
							>
								{strings.notAnswered || 'Ikke besvart'}
							</button>
							<button
								type="button"
								disabled={disableDelayButtons}
								className={
									'sm-btn ' +
									(del.code === 'D' ? 'sm-btn--primary' : '')
								}
								onClick={() => this.setScore('delayed', 'D')}
							>
								{strings.wrong || 'Feil'}
							</button>
						</div>
					</div>
				</div>

				<div className="nw-admin-footer">
					<p className="nw-admin-total">
						{strings.nonword_item_total || 'Poeng for oppgaven'}:{' '}
						{(itemRes.total != null ? itemRes.total : 0) + ' / 2'}
					</p>
					<div className={`adminItemButtons`}>
						<button
							type="button"
							className="sm-btn sm-btn--primary"
							disabled={!bothAnswered}
							onClick={this.goNextItem}
						>
							{strings.nextTask || 'Neste oppgave'}
						</button>
					</div>
				</div>
			</div>
		);
	};

	renderWaiting = () => (
		<div>
			<p>
				{strings.formatString(
					strings.testDone || 'Testen {0} er ferdig.',
					strings.nonword_testname || 'Nonordrepetisjon'
				)}
			</p>
			{/* <button
        type="button"
        className="sm-btn sm-btn--primary"
        onClick={() => this.props.submitResults(this.state.results)}
      >
        {strings.submitResults || 'Lagre resultater'}
      </button> */}
			<button
				type="button"
				className="sm-btn sm-btn--ghost"
				onClick={this.props.nextStage}
			>
				{strings.nextTest || 'Neste deltest'}
			</button>
		</div>
	);

	// ---------- main render ----------

	render() {
		const {currentStage} = this.state;

		return (
			<div
				className="lit-intro NonwordRepetitionAdmin"
				tabIndex={0}
				ref={(el) => (this.rootEl = el)}
				// onKeyDown={this.handleKeyDown}
			>
				<h2 className="lit-page-title">
					{strings.nonword_testname || 'Nonordrepetisjon'}
				</h2>

				{currentStage === NW_STAGES.INTRO && this.renderIntro()}
				{currentStage === NW_STAGES.TRIAL && this.renderTrial()}
				{currentStage === NW_STAGES.WAITING_POST_TRIAL &&
					this.renderWaitingPostTrial()}
				{currentStage === NW_STAGES.TEST && this.renderTest()}
				{currentStage === NW_STAGES.WAITING && this.renderWaiting()}
			</div>
		);
	}
}

export default NonwordRepetitionAdmin;
